import React, { useState } from 'react';
import { Plane, ArrowRight, Loader2, CheckCircle2, AlertTriangle, MapPin, Calendar, Home, Car, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEligibilityLimit } from '../../hooks/useEligibilityLimit';

type Step = 'input' | 'thinking' | 'result';

interface ArrivalResult {
    readinessScore: number;
    missingItems: string[];
    verdict: string;
}

const TravelForm = () => { // Renamed conceptually to ArrivalPlanner, keeping file name for compatibility
    const navigate = useNavigate();
    const { canAccess, loading, markAsUsed } = useEligibilityLimit('travel');
    const [step, setStep] = useState<Step>('input');
    const [result, setResult] = useState<ArrivalResult | null>(null);

    // Form State
    const [destination, setDestination] = useState('United Kingdom (UK)');
    const [timeline, setTimeline] = useState('Within 30 days');
    const [accommodation, setAccommodation] = useState('No, I haven’t booked yet');
    const [transport, setTransport] = useState('I need airport pickup');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStep('thinking');

        try {
            const response = await fetch('/api/predict-arrival', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ destination, timeline, accommodation, transport })
            });

            const data = await response.json();

            // Simulate thinking time for effect
            setTimeout(() => {
                setResult(data);
                setStep('result');
                markAsUsed();
            }, 2500);

        } catch (error) {
            console.error("Arrival prediction failed", error);
            // Fallback for demo
            setTimeout(() => {
                setResult({
                    readinessScore: 68,
                    missingItems: ["Accommodation not confirmed", "Airport transport not planned", "Arrival timeline is close"],
                    verdict: "You’re on track, but a few key steps are missing."
                });
                setStep('result');
                markAsUsed();
            }, 2500);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-400">Loading Access Rights...</div>;

    if (!canAccess) {
        return (
            <div className="flex flex-col items-center justify-center text-center p-8 space-y-6">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={32} className="text-gray-400" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-[#011E26]">Free Planner Limit Reached</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                        You have already used your free arrival plan. Please sign in or create an account to access unlimited AI plans and support.
                    </p>
                </div>
                <div className="flex gap-4">
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-3 bg-white border border-gray-200 text-[#011E26] font-bold rounded hover:border-[#009B8F] transition-all"
                    >
                        Sign In
                    </button>
                    <button
                        onClick={() => navigate('/signup')}
                        className="px-6 py-3 bg-[#009B8F] text-white font-bold rounded hover:bg-[#00857a] transition-all"
                    >
                        Create Free Account
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'thinking') {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] animate-fadeIn">
                <Loader2 size={48} className="text-[#009B8F] animate-spin mb-6" />
                <h3 className="text-xl font-serif text-[#011E26] animate-pulse">Analyzing Arrival Logistics...</h3>
                <p className="text-slate-400 mt-2 text-sm uppercase tracking-widest">Checking Accommodation & Transport Nodes</p>
            </div>
        );
    }

    if (step === 'result' && result) {
        return (
            <div className="animate-fadeIn max-w-lg mx-auto">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
                        <Plane size={14} /> Logistics Analysis Complete
                    </div>
                    <h3 className="text-2xl font-serif text-[#011E26]">Your Arrival Readiness Score</h3>
                </div>

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    {/* Score Header */}
                    <div className={`p-8 text-center text-white relative overflow-hidden ${result.readinessScore > 75 ? 'bg-green-600' : 'bg-[#D97706]'}`}>
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full filter blur-[50px] opacity-20"></div>
                        <h1 className="text-6xl font-bold mb-2">{result.readinessScore}%</h1>
                        <p className="text-white/90 font-medium text-lg px-4">
                            "{result.verdict}"
                        </p>
                    </div>

                    {/* Missing Items */}
                    <div className="p-8 bg-gray-50 border-b border-gray-100">
                        <h4 className="font-bold text-[#011E26] mb-4 flex items-center gap-2">
                            <AlertTriangle size={18} className="text-[#D97706]" /> Key Missing Items
                        </h4>
                        <ul className="space-y-3">
                            {result.missingItems.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 bg-white p-3 rounded shadow-sm border border-gray-100">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0"></div>
                                    <span className="text-slate-600 text-sm">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Upgrade Message & CTA */}
                    <div className="p-8 text-center bg-white">
                        <p className="text-[#011E26] font-medium mb-6 leading-relaxed">
                            “Sign up to get a detailed curated plan, organise your journey, and receive exclusive student discounts.”
                        </p>

                        <button
                            onClick={() => navigate('/signup')} // Direct to signup as requested
                            className="w-full py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all shadow-lg flex items-center justify-center gap-2 group"
                        >
                            Get My Curated Plan <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-8 animate-fadeIn">
            <div className="mb-8">
                <h3 className="text-2xl font-serif text-[#011E26] mb-2">Arrival Planner</h3>
                <p className="text-slate-600">Plan your journey from landing to settling in with AI precision.</p>
            </div>

            {/* 1. Destination */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2 flex items-center gap-2"><MapPin size={14} /> Destination Country</label>
                <select
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <option>United Kingdom (UK)</option>
                    <option>United States (USA)</option>
                    <option>Canada</option>
                    <option>Australia</option>
                    <option>New Zealand</option>
                    <option>Europe (Schengen)</option>
                </select>
            </div>

            {/* 2. Timeline */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2 flex items-center gap-2"><Calendar size={14} /> Planned Arrival Timeline</label>
                <select
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <option>Within 30 days</option>
                    <option>1–3 months</option>
                    <option>3–6 months</option>
                    <option>Not sure yet</option>
                </select>
            </div>

            {/* 3. Accommodation */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2 flex items-center gap-2"><Home size={14} /> Accommodation Status</label>
                <select
                    value={accommodation}
                    onChange={(e) => setAccommodation(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <option>Yes, I have confirmed accommodation</option>
                    <option>No, I haven’t booked yet</option>
                    <option>I am still searching</option>
                </select>
            </div>

            {/* 4. Transport */}
            <div>
                <label className="text-xs font-bold text-[#011E26] uppercase tracking-wider block mb-2 flex items-center gap-2"><Car size={14} /> Airport Transport Preference</label>
                <select
                    value={transport}
                    onChange={(e) => setTransport(e.target.value)}
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded focus:border-[#009B8F] outline-none transition-colors"
                >
                    <option>I need airport pickup</option>
                    <option>I will manage myself</option>
                    <option>Not sure yet</option>
                </select>
            </div>

            <button
                type="submit"
                className="w-full py-4 bg-[#011E26] hover:bg-[#009B8F] text-white font-bold text-sm uppercase tracking-widest rounded transition-all flex items-center justify-center gap-2"
            >
                <Plane size={16} /> Run AI Prediction
            </button>
        </form>
    );
};

export default TravelForm;
